import { useCallback, useEffect, useRef, useState } from "react";

// iOS Safari needs srcObject + explicit play() (autoplay alone is unreliable).
function attachStream(el, stream) {
  if (el.srcObject !== stream) el.srcObject = stream;
  const p = el.play?.();
  if (p?.catch) p.catch((e) => console.warn("video play blocked", e));
}

// Owns the MediaStream lifecycle: start when enabled, restart on facing change, stop on unmount.
export function useCamera(enabled, facing) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(null);

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    stop();
    const bind = (s) => {
      if (!active) return s.getTracks().forEach((t) => t.stop());
      streamRef.current = s;
      if (videoRef.current) attachStream(videoRef.current, s);
      setReady(true);
    };
    const primary = { video: { facingMode: { ideal: facing }, width: { ideal: 1280 }, height: { ideal: 1600 } }, audio: false };
    navigator.mediaDevices
      .getUserMedia(primary)
      .then(bind)
      .catch((e) => {
        console.warn("camera primary constraints failed, retrying simple", e);
        // iOS Safari / older Android: fall back to the simplest constraint set.
        return navigator.mediaDevices.getUserMedia({ video: { facingMode: facing }, audio: false }).catch(() => navigator.mediaDevices.getUserMedia({ video: true, audio: false })).then(bind);
      })
      .catch((e) => {
        console.warn("camera unavailable", e);
        if (active) setReady(false);
      });
    return () => {
      active = false;
      stop();
    };
  }, [enabled, facing, stop]);

  useEffect(() => {
    if (ready && videoRef.current && streamRef.current) attachStream(videoRef.current, streamRef.current);
  }, [ready]);

  // Callback ref: binds the stream as soon as the <video> mounts, regardless of effect ordering.
  const attachVideo = useCallback((el) => {
    videoRef.current = el;
    if (el && streamRef.current) attachStream(el, streamRef.current);
  }, []);

  const captureBlob = useCallback(
    (mirror) =>
      new Promise((resolve) => {
        const v = videoRef.current;
        if (!v || !v.videoWidth || !v.videoHeight) return resolve(null);
        const c = document.createElement("canvas");
        c.width = v.videoWidth;
        c.height = v.videoHeight;
        const ctx = c.getContext("2d");
        if (mirror) {
          ctx.translate(c.width, 0);
          ctx.scale(-1, 1);
        }
        ctx.drawImage(v, 0, 0, c.width, c.height);
        c.toBlob(resolve, "image/jpeg", 0.92);
      }),
    [],
  );

  return { videoRef, attachVideo, ready, stop, captureBlob };
}
